import { MAX_COLUMNS, MIN_COLUMNS } from '@miyuki/domain';
import type { PatternType } from '@miyuki/types';
import { Button, Input, Label, Sheet, SheetContent, SheetHeader, SheetTitle } from '@miyuki/ui';
import * as React from 'react';

import { PatternTypePicker } from './pattern-type-picker';

import { t } from '@/lib/i18n';

/**
 * Ajustes de un diseño ya empezado: su ancho y su tejido.
 *
 * Vive en un panel y no en la pantalla porque no se usa mientras se dibuja
 * (RF-49 de 001). Los valores fuera de rango se rechazan con el motivo escrito,
 * no deshabilitando el botón sin explicación.
 */
export interface DesignSettingsSheetProps {
  readonly open: boolean;
  readonly columns: number;
  readonly patternType: PatternType;
  /**
   * Los dos valores viajan juntos a propósito: la usuaria pulsó «Aplicar» una
   * vez, así que deshacer debe revertirlo entero (RF-10c).
   */
  readonly onApply: (columns: number, patternType: PatternType) => void;
  readonly onClose: () => void;
}

function parseCount(value: string): number {
  return Number.parseInt(value, 10);
}

export function DesignSettingsSheet({
  open,
  columns,
  patternType,
  onApply,
  onClose,
}: DesignSettingsSheetProps) {
  const [draft, setDraft] = React.useState(String(columns));
  const [draftType, setDraftType] = React.useState<PatternType>(patternType);
  const [wasOpen, setWasOpen] = React.useState(open);

  // Al reabrir el panel, el campo parte del ancho vigente y no del último
  // intento abandonado. Se ajusta durante el render y no en un efecto: así no
  // hay un primer render con el valor viejo ni una cascada de renderizados.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraft(String(columns));
      setDraftType(patternType);
    }
  }

  const parsed = parseCount(draft);
  const error =
    Number.isInteger(parsed) && parsed >= MIN_COLUMNS && parsed <= MAX_COLUMNS
      ? null
      : t('columnsOutOfRange');

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent side="bottom" className="pb-8">
        <SheetHeader>
          <SheetTitle>{t('settings')}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-4 pb-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="settings-pattern-type">{t('patternType')}</Label>
            <PatternTypePicker
              id="settings-pattern-type"
              value={draftType}
              onChange={setDraftType}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="settings-columns">{t('columns')}</Label>
            <Input
              id="settings-columns"
              type="number"
              inputMode="numeric"
              min={MIN_COLUMNS}
              max={MAX_COLUMNS}
              className="h-11"
              value={draft}
              aria-invalid={error !== null}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <Button
            size="touch"
            disabled={error !== null}
            onClick={() => {
              onApply(parsed, draftType);
            }}
          >
            {t('apply')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
